import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { Box, Checkbox, Chip, Grid, Link, MenuItem, TextField, Typography } from '@mui/material';
import { RiCheckboxBlankLine, RiCheckboxFill } from 'react-icons/ri';
import { useTheme } from '@mui/styles';
import { api } from 'common/frontend/api';
import BackdropDialog from 'common/components/BackdropDialog';
import { ChurnReason } from 'common/services/analytics/tractionAnalytics';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { t, Trans } from 'services/localization';

type InputEventType = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

type ChurnOption = {
	type: ChurnReason;
	title: string;
};
interface Props {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
}

const MENU_HEIGHT = 80;
const MENU_PADDING_TOP = 8;

const ChurnModal = (props: Props) => {
	const { isOpen, onConfirm, onClose } = props;
	const theme = useTheme();
	const [reasonsForDowngrade, setReasonsForDowngrade] = useState<ChurnReason[]>([]);
	const [missingFeatures, setMissingFeatures] = useState<string | undefined>();
	const [nextPlatform, setNextPlatform] = useState<string | undefined>();
	const [additionalInformation, setAdditionalInformation] = useState<string | undefined>();
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const analyticsDocId = shopAnalytics?.id;
	const hasFeaturesAsReason = reasonsForDowngrade.includes('FEATURES');
	const hasOtherPlatformAsReason = reasonsForDowngrade.includes('OTHER_PLATFORM');

	const downgradeReasons: ChurnOption[] = [
		{
			type: 'FEATURES',
			title: t(
				'account.billing.plan.churnModal.downgradeReasonFeatures',
				'Rentle doesn’t have the features my business needs',
			),
		},
		{
			type: 'PRICE',
			title: t('account.billing.plan.churnModal.downgradeReasonPrice', 'Rentle is too expensive'),
		},
		{
			type: 'SALES',
			title: t(
				'account.billing.plan.churnModal.downgradeReasonSales',
				'The business is not making enough sales',
			),
		},
		{
			type: 'SETTING_UP',
			title: t(
				'account.billing.plan.churnModal.downgradeReasonSettingUp',
				'The Rentle store is too difficult to set up or launch',
			),
		},
		{
			type: 'TEMPORARY_USE',
			title: t(
				'account.billing.plan.churnModal.downgradeReasonTemporaryUse',
				'The business was opened for temporary use',
			),
		},
		{
			type: 'OTHER_PLATFORM',
			title: t(
				'account.billing.plan.churnModal.downgradeReasonOtherPlatform',
				'The business is switching to another platform',
			),
		},
		{
			type: 'CLOSING_STORE',
			title: t(
				'account.billing.plan.churnModal.downgradeReasonClosed',
				'The business is permanently closed',
			),
		},
		{
			type: 'OTHER_REASON_FOR_DOWNGRADE',
			title: t('account.billing.plan.churnModal.downgradeReasonOther', 'Other reason'),
		},
	];

	const confirmChurn = async () => {
		if (!reasonsForDowngrade.length) return;
		if (analyticsDocId) {
			const churnObject = {
				timestamp: moment().toISOString(),
				reasonsForDowngrade,
				...(!!missingFeatures && { missingFeatures }),
				...(!!nextPlatform && { nextPlatform }),
				...(!!additionalInformation && { additionalInformation }),
			};
			await api().shopAnalytics.doc(analyticsDocId).update({ churn: churnObject });
		}
		onConfirm();
	};

	const handleReasonForDowngradeChange = (e: InputEventType) => {
		// as typescript thinks the value is a string instead of type ChurnReason[]
		const downgradeReasons = (e.target.value as unknown) as ChurnReason[];
		setReasonsForDowngrade(downgradeReasons);
	};

	const handleDeleteReasonForDowngrade = (deletedReason: ChurnReason) => {
		const updatedValue = reasonsForDowngrade.filter(
			(reasonForDowngrade) => reasonForDowngrade !== deletedReason,
		);
		setReasonsForDowngrade(updatedValue);
	};

	const renderReasonToDowngradeValue = (selected: ChurnReason[]) => (
		<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
			{selected.map((val) => {
				return (
					<Chip
						key={val}
						label={downgradeReasons.find((reason) => reason.type === val)?.title}
						onDelete={() => handleDeleteReasonForDowngrade(val)}
						onMouseDown={(event) => {
							event.stopPropagation();
						}}
						sx={{
							m: '2px',
						}}
					/>
				);
			})}
		</Box>
	);

	const renderReasonToDowngrade = () => {
		return (
			<Grid item xs={12} sx={{ mb: 4 }}>
				<TextField
					id="reasonForDowngrade"
					name="reasonForDowngrade"
					value={reasonsForDowngrade}
					select
					fullWidth
					multiline
					required
					label={t(
						'account.billing.plan.churnModal.reasonsForDowngrading',
						'What are the reasons you’re downgrading?',
					)}
					onChange={handleReasonForDowngradeChange}
					SelectProps={{
						multiple: true,
						MenuProps: {
							sx: {
								maxHeight: MENU_HEIGHT * 4.5 + MENU_PADDING_TOP,
								width: 250,
							},
							variant: 'menu',
							MenuListProps: {
								sx: {
									overflowX: 'scroll',
								},
							},
						},
						renderValue: (selected) => renderReasonToDowngradeValue(selected as ChurnReason[]),
					}}
					sx={{
						minWidth: 200,
						[theme.breakpoints.down('md')]: {
							ml: 0,
						},
					}}
				>
					<MenuItem value="default" disabled>
						{t(
							'account.billing.plan.churnModal.selectReasonsForDowngrade',
							'Select the reasons for the downgrade',
						)}
					</MenuItem>
					{downgradeReasons.map((downgradeReason) => (
						<MenuItem value={downgradeReason.type} key={downgradeReason.type}>
							<Checkbox
								icon={<RiCheckboxBlankLine fontSize="small" />}
								color="primary"
								checkedIcon={<RiCheckboxFill fontSize="small" />}
								sx={{ mr: 1 }}
								checked={reasonsForDowngrade.includes(downgradeReason.type)}
							/>
							{downgradeReason.title}
						</MenuItem>
					))}
				</TextField>
				{hasFeaturesAsReason && (
					<TextField
						variant="outlined"
						name="reasonForDowngradeFollowUp"
						value={missingFeatures}
						label={t(
							'account.billing.plan.churnModal.whichFeaturesNeed',
							'Which features do you need?',
						)}
						onChange={(e: InputEventType) => setMissingFeatures(e.target.value)}
						fullWidth
						multiline
						sx={{
							mt: theme.spacing(2),
						}}
						required
					/>
				)}
				{hasOtherPlatformAsReason && (
					<TextField
						variant="outlined"
						name="reasonForDowngradeFollowUp"
						value={nextPlatform}
						label={t(
							'account.billing.plan.churnModal.wichPlatformMovingTo',
							'Which platform are you moving to?',
						)}
						onChange={(e: InputEventType) => setNextPlatform(e.target.value)}
						fullWidth
						multiline
						sx={{
							mt: theme.spacing(2),
						}}
						required
					/>
				)}
				{!!reasonsForDowngrade.length && (
					<TextField
						variant="outlined"
						name="reasonForDowngradeFollowUp"
						value={additionalInformation}
						label={t(
							'account.billing.plan.churnModal.describeYourReasonsToDowngrade',
							'Describe your reasons to downgrade',
						)}
						onChange={(e: InputEventType) => setAdditionalInformation(e.target.value)}
						fullWidth
						multiline
						sx={{
							mt: theme.spacing(2),
						}}
						required
					/>
				)}
			</Grid>
		);
	};

	const renderModalContent = () => {
		return (
			<>
				{renderReasonToDowngrade()}
				<Typography variant="body2">
					<Trans
						i18nKey="account.billing.plan.churnModal.contactSupport"
						defaults="If you need help, please contact <supportLink>Rentle support</supportLink>."
						components={{
							supportLink: (
								<Link
									href="mailto:cgregytq@rentle.intercom-mail.com"
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										color: theme.palette.primary.main,
										fontWeight: 400,
									}}
								>
									{'Rentle support'}
								</Link>
							),
						}}
					/>
				</Typography>
			</>
		);
	};

	return (
		<BackdropDialog
			variant="primary"
			isOpen={isOpen}
			onClose={onClose}
			disableCloseOnClickAway={true}
			width={'sm'}
			label={t('account.billing.plan.churnModal.downgradeToRentleFree', 'Downgrade to Rentle Free')}
			body={renderModalContent()}
			confirm={{
				label: t('account.billing.plan.churnModal.confirmDowngrade', 'Confirm downgrade'),
				callback: () => confirmChurn(),
				disabled:
					!reasonsForDowngrade?.length ||
					(hasFeaturesAsReason && !missingFeatures) ||
					(hasOtherPlatformAsReason && !nextPlatform) ||
					!additionalInformation,
				variant: 'contained',
				closeAfterDone: true,
			}}
			cancel={{
				label: t('common:actions.back', 'Back'),
				callback: onClose,
				autoFocus: false,
			}}
		/>
	);
};

export default ChurnModal;
